// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import { handleApiCall, buildParams } from "../utils/apiHelper";
import tenantService from "./tenantService";

const locationService = {
  /**
   * Fetch location overview with summary metrics
   * GET /admin/locations/overview?page=1&limit=25
   */
  async getLocationsOverview(params = {}) {
    const queryParams = buildParams({
      page: params.page || 1,
      limit: params.limit || 25,
      tenant_id: params.tenant_id,
      search: params.search,
    });

    return handleApiCall(
      () => apiClient.get("/admin/locations/overview", { params: queryParams }),
      "Error fetching locations overview",
    );
  },

  /**
   * Fetch locations with pagination and sorting
   * GET /admin/locations/?page=1&limit=100&sort_by=name&sort_order=asc
   */
  async getLocations(params = {}) {
    const queryParams = buildParams({
      page: params.page || 1,
      limit: params.limit || 100,
      sort_by: params.sort_by || "name",
      sort_order: params.sort_order || "asc",
      search: params.search,
    });

    return handleApiCall(
      () => apiClient.get("/admin/locations/", { params: queryParams }),
      "Error fetching locations",
    );
  },

  /**
   * Get location by ID
   */
  async getLocationById(locationId) {
    return handleApiCall(
      () => apiClient.get(`/admin/locations/${locationId}`),
      `Error fetching location ${locationId}`,
    );
  },

  /**
   * Create a new location
   */
  async createLocation(locationData) {
    return handleApiCall(
      () => apiClient.post("/admin/locations/", locationData),
      "Error creating location",
    );
  },

  /**
   * Update location
   */
  async updateLocation(locationId, locationData) {
    return handleApiCall(
      () => apiClient.put(`/admin/locations/${locationId}`, locationData),
      `Error updating location ${locationId}`,
    );
  },

  /**
   * Delete location
   */
  async deleteLocation(locationId) {
    return handleApiCall(
      () => apiClient.delete(`/admin/locations/${locationId}`),
      `Error deleting location ${locationId}`,
    );
  },

  /**
   * Inverse of `tenantService.getTenantLocations` — given a locationId,
   * return the list of tenants currently assigned to it. The backend
   * doesn't yet expose `GET /admin/locations/{id}/tenants` (TODO), so we
   * fall back to iterating tenants and checking each one's location list.
   */
  async getLocationTenants(locationId, options = {}) {
    const { tenants } = options;
    let allTenants = tenants;
    if (!Array.isArray(allTenants)) {
      const resp = await tenantService.getAllTenantsForFilter({
        includeAll: false,
        limit: 500,
      });
      allTenants = Array.isArray(resp) ? resp : [];
    }
    const assignments = [];
    await Promise.all(
      allTenants.map(async (t) => {
        try {
          const tenantLocations = await tenantService.getTenantLocations(t.id);
          const rows = Array.isArray(tenantLocations)
            ? tenantLocations
            : (tenantLocations?.items ??
              tenantLocations?.data ??
              tenantLocations?.locations ??
              []);
          const match = rows.find(
            (r) =>
              String(r.id ?? r.location_id) === String(locationId) ||
              String(r.location?.id) === String(locationId),
          );
          if (match) {
            assignments.push({
              tenant_id: t.id,
              tenant_name: t.name,
              role: match.role ?? "operator",
              is_primary: match.is_primary ?? false,
            });
          }
        } catch (err) {
          console.warn(`Could not load locations for tenant ${t.id}`, err);
        }
      }),
    );
    return assignments;
  },
};

export default locationService;
